/**
 * The revenue & capacity planner — the arithmetic, and nothing else.
 *
 * STANDALONE BY DESIGN. Planner entries are not Project Hub projects, clients
 * or time entries, and nothing here reads or writes those. Rates are typed into
 * the planner itself. That keeps "what if I win this bid" modelling from ever
 * touching what you actually bill, and means the planner can be removed without
 * a trace in the billing data.
 *
 * TIME IS DERIVED, NEVER STORED. An entry holds its parameters as a short list
 * of dated segments ("from 2027-01, $13,333/mo at 7 h/wk"); the month grid is
 * computed from them on demand over whatever window is on screen. So a new year
 * is just a wider window and a mid-year raise is one more segment — there are
 * no month columns to add and nothing to migrate.
 *
 * NO IMPORTS on purpose: `npm test` runs this file directly under Node's type
 * stripping, without the bundler's `@/` aliases.
 */

/** "2026-10". Sorts and compares correctly as a plain string. */
export type MonthKey = string;

/** Retainer and fixed fee both bill a monthly amount; the kind is a label. */
export type PlanKind = "retainer" | "fixed" | "hourly";
export type PlanStatus = "confirmed" | "potential";

export interface Segment {
  /** Applies from this month until the next segment starts. */
  from: MonthKey;
  /** Retainer / fixed fee: $ billed that month. */
  monthlyAmount?: number;
  /** Hourly: $/h. */
  hourlyRate?: number;
  /** Every kind: the workload. */
  hoursPerWeek?: number;
}

export interface PlanEntry {
  id: string;
  name: string;
  kind: PlanKind;
  status: PlanStatus;
  /** First and last billed month, inclusive. */
  start: MonthKey;
  end: MonthKey;
  /** Sorted by `from`, at least one. */
  segments: Segment[];
  position?: number;
}

/** A value that can change from a given month: capacity. */
export interface Dated { from: MonthKey; value: number }

export interface PlanSettings {
  /** Available hours per week, effective-dated. */
  capacity: Dated[];
  /** Annual revenue goal by year ("2027"). A year with none uses the latest earlier one. */
  goals: Record<string, number>;
}

/** 52 weeks over 12 months. */
export const WPM = 52 / 12;

/* -- Month keys -------------------------------------------------------------- */

export const toKey = (y: number, m: number): MonthKey => `${y}-${String(m).padStart(2, "0")}`;

export function addMonths(k: MonthKey, n: number): MonthKey {
  const [y, m] = k.split("-").map(Number);
  const i = y * 12 + (m - 1) + n;
  return toKey(Math.floor(i / 12), (i % 12) + 1);
}

export function monthsBetween(start: MonthKey, end: MonthKey): MonthKey[] {
  const out: MonthKey[] = [];
  for (let k = start; k <= end; k = addMonths(k, 1)) out.push(k);
  return out;
}

export const yearOf = (k: MonthKey): string => k.slice(0, 4);

/**
 * A calendar year's months, from `from` onward: the whole year when it starts
 * later, the rest of it when `from` falls inside it.
 */
export function yearMonths(year: string, from: MonthKey): MonthKey[] {
  const first = `${year}-01`, last = `${year}-12`;
  return from > last ? [] : monthsBetween(from > first ? from : first, last);
}

export const currentMonth = (d = new Date()): MonthKey => toKey(d.getFullYear(), d.getMonth() + 1);

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Oct" — the column heading; the year sits in the row above it. */
export const monthLabel = (k: MonthKey): string => MONTHS[Number(k.slice(5, 7)) - 1];

/** "Oct 2026" — anywhere a month stands alone. */
export const monthYearLabel = (k: MonthKey): string => `${monthLabel(k)} ${yearOf(k)}`;

/* -- Engine ------------------------------------------------------------------ */

/** The segment in effect for a month: the last whose `from` is on or before it. */
export function segmentFor(p: PlanEntry, month: MonthKey): Segment | null {
  let cur: Segment | null = null;
  for (const s of p.segments) {
    if (s.from <= month) cur = s;
    else break;
  }
  return cur;
}

export interface Cell { revenue: number; hoursPerWeek: number }

export function cellFor(p: PlanEntry, month: MonthKey): Cell {
  if (month < p.start || month > p.end) return { revenue: 0, hoursPerWeek: 0 };
  const s = segmentFor(p, month);
  if (!s) return { revenue: 0, hoursPerWeek: 0 };
  const hoursPerWeek = s.hoursPerWeek ?? 0;
  const revenue = p.kind === "hourly"
    ? hoursPerWeek * WPM * (s.hourlyRate ?? 0)
    : (s.monthlyAmount ?? 0);
  return { revenue, hoursPerWeek };
}

/**
 * What a month actually pays per hour worked. Meaningful for monthly-amount
 * work, where the rate is an outcome rather than an input. Named apart from
 * hooks/useTime's `effectiveRate`, which resolves a billing rate.
 */
export function earnedRate(p: PlanEntry, month: MonthKey): number | null {
  const { revenue, hoursPerWeek } = cellFor(p, month);
  return hoursPerWeek > 0 ? revenue / (hoursPerWeek * WPM) : null;
}

/** The same across a window: total revenue over total hours. */
export function earnedRateOver(p: PlanEntry, months: MonthKey[]): number | null {
  let revenue = 0, hours = 0;
  for (const m of months) {
    const c = cellFor(p, m);
    revenue += c.revenue;
    hours += c.hoursPerWeek * WPM;
  }
  return hours > 0 ? revenue / hours : null;
}

export const projectTotal = (p: PlanEntry, months: MonthKey[]): number =>
  months.reduce((sum, m) => sum + cellFor(p, m).revenue, 0);

export function datedValue(list: Dated[], month: MonthKey): number {
  let cur = 0;
  for (const d of [...list].sort((a, b) => a.from.localeCompare(b.from))) {
    if (d.from <= month) cur = d.value;
    else break;
  }
  return cur;
}

export function goalFor(goals: Record<string, number>, year: string): number {
  const years = Object.keys(goals).filter((y) => y <= year && goals[y] > 0).sort();
  return years.length ? goals[years[years.length - 1]] : 0;
}

/* -- Aggregations ------------------------------------------------------------ */

export interface MonthTotal {
  month: MonthKey;
  confirmedRev: number;
  allRev: number;
  /** Booked hours per week. */
  confirmedHrs: number;
  allHrs: number;
  /** Available hours per week that month. */
  capacity: number;
}

export function monthTotals(entries: PlanEntry[], months: MonthKey[], capacity: Dated[] = []): MonthTotal[] {
  return months.map((month) => {
    let confirmedRev = 0, allRev = 0, confirmedHrs = 0, allHrs = 0;
    for (const p of entries) {
      const { revenue, hoursPerWeek } = cellFor(p, month);
      allRev += revenue;
      allHrs += hoursPerWeek;
      if (p.status === "confirmed") { confirmedRev += revenue; confirmedHrs += hoursPerWeek; }
    }
    return { month, confirmedRev, allRev, confirmedHrs, allHrs, capacity: datedValue(capacity, month) };
  });
}

export interface YearTotal {
  year: string;
  /** How many of the year's months are in the window — a partial year is labelled as one. */
  months: number;
  confirmedRev: number;
  allRev: number;
  goal: number;
}

export function yearTotals(rows: MonthTotal[], goals: Record<string, number> = {}): YearTotal[] {
  const out = new Map<string, YearTotal>();
  for (const r of rows) {
    const year = yearOf(r.month);
    const t = out.get(year) ?? { year, months: 0, confirmedRev: 0, allRev: 0, goal: goalFor(goals, year) };
    t.months += 1;
    t.confirmedRev += r.confirmedRev;
    t.allRev += r.allRev;
    out.set(year, t);
  }
  return [...out.values()];
}

/* -- Ordering ---------------------------------------------------------------- */

/**
 * Confirmed work first, then potential; each by start month, then name. What
 * you have won is what you plan around, so it leads; bids sit underneath.
 */
export function autoSort<T extends Pick<PlanEntry, "status" | "start" | "name">>(entries: T[]): T[] {
  const rank = (s: PlanStatus) => (s === "confirmed" ? 0 : 1);
  return [...entries].sort((a, b) =>
    rank(a.status) - rank(b.status)
    || a.start.localeCompare(b.start)
    || a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}

/** Manual order: by stored position, ties (and unset) after, stable. */
export function manualSort<T extends Pick<PlanEntry, "position">>(entries: T[]): T[] {
  return entries
    .map((e, i) => ({ e, i }))
    .sort((a, b) => (a.e.position ?? Infinity) - (b.e.position ?? Infinity) || a.i - b.i)
    .map(({ e }) => e);
}

/** Move one item a step up or down; returns a new array. */
export function moveItem<T>(list: T[], index: number, dir: -1 | 1): T[] {
  const to = index + dir;
  if (to < 0 || to >= list.length) return list;
  const out = [...list];
  [out[index], out[to]] = [out[to], out[index]];
  return out;
}

/* -- Editing ----------------------------------------------------------------- */

const sameValues = (a: Segment, b: Segment) =>
  (a.monthlyAmount ?? null) === (b.monthlyAmount ?? null)
  && (a.hourlyRate ?? null) === (b.hourlyRate ?? null)
  && (a.hoursPerWeek ?? null) === (b.hoursPerWeek ?? null);

/**
 * Sorted, one segment per month, and no segment that merely repeats the one
 * before it. Run after every edit: it is what makes removing a one-month
 * change also remove the "restore" segment that followed it.
 */
export function normalizeSegments(segments: Segment[]): Segment[] {
  const byMonth = new Map<MonthKey, Segment>();
  for (const s of segments) byMonth.set(s.from, s);
  const sorted = [...byMonth.values()].sort((a, b) => a.from.localeCompare(b.from));
  return sorted.filter((s, i) => i === 0 || !sameValues(s, sorted[i - 1]));
}

/**
 * Change one month.
 *
 * "month" changes that month only: the values in effect afterwards are
 * restored the month after, so a lumpy fixed-fee invoice or a light month does
 * not quietly rewrite the rest of the engagement. "onward" is a raise or a
 * rescope: it holds until the next scheduled change.
 */
export function setMonthOverride(
  p: PlanEntry,
  month: MonthKey,
  patch: Partial<Omit<Segment, "from">>,
  scope: "month" | "onward" = "month",
): PlanEntry {
  const base = segmentFor(p, month) ?? { from: month };
  const next: Segment[] = p.segments.filter((s) => s.from !== month);
  next.push({ ...base, ...patch, from: month });

  const following = addMonths(month, 1);
  if (scope === "month" && following <= p.end && !p.segments.some((s) => s.from === following)) {
    const after = segmentFor(p, following) ?? base;
    next.push({ ...after, from: following });
  }
  return { ...p, segments: normalizeSegments(next) };
}

/** Undo a change that starts at `month`; the first segment cannot be removed. */
export function removeSegmentAt(p: PlanEntry, month: MonthKey): PlanEntry {
  if (p.segments[0]?.from === month) return p;
  return { ...p, segments: normalizeSegments(p.segments.filter((s) => s.from !== month)) };
}

/**
 * The first segment always starts on the entry's start month, so moving the
 * start date never leaves the opening months with no values.
 */
export function anchorSegments(p: PlanEntry): PlanEntry {
  if (p.segments.length === 0) return { ...p, segments: [{ from: p.start }] };
  const sorted = normalizeSegments(p.segments);
  const inRange = sorted.filter((s) => s.from > p.start);
  const opening = segmentFor({ ...p, segments: sorted }, p.start) ?? sorted[0];
  return { ...p, segments: normalizeSegments([{ ...opening, from: p.start }, ...inRange]) };
}
